import React, { useEffect, useState, useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'
import { IconSparkles } from '@tabler/icons-react'

import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Checkbox'
import { Accordion } from '@/components/accordions/Accordion'
import { Pill } from '@/components/pills/Pill'
import { Button } from '@/components/buttons/Button'
import { reorder } from '@/utils/helpers'

import { Drag, DragAndDrop, Drop } from '../../dragDrop'

import './EditColumnsForm.scss'

// extra columns are the inner columns
export const EditColumnsForm = ({
  handleSubmit,
  allColumns,
  extraColumns = [],
  loading,
  onClose,
}) => {
  const [tempColumns, setTempColumns] = useState(allColumns)
  const [innerColumnsGroupedByParent, setInnerColumnsGroupedByParent] = useState([])
  const [toggleAll, setToggleAll] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndexes, setActiveIndexes] = useState([])
  const [outerColumnsSearchable, setOuterColumnsSearchable] = useState(allColumns)
  const [innerColumnsSearchable, setInnerColumnsSearchable] = useState([])
  const parentAccessors = useMemo(() => {
    if (isEmpty(extraColumns)) return ['scores', 'tags']
    const accessors = Object.keys(
      extraColumns.reduce((acc, column) => {
        acc[column.parent] = true
        return acc
      }, {})
    )
    if (!accessors.includes('scores')) {
      accessors.push('scores')
    }
    if (!accessors.includes('tags')) {
      accessors.push('tags')
    }
    return accessors
  }, [extraColumns])

  useEffect(() => {
    if (!isEmpty(extraColumns)) {
      const groupedColumns = extraColumns.reduce((acc, column) => {
        const parentAccessor = column.parent
        if (!acc[parentAccessor]) {
          acc[parentAccessor] = []
        }
        column.is_hidden = !column.isParent ? column.is_hidden : false
        acc[parentAccessor].push(column)
        return acc
      }, {})
      setInnerColumnsGroupedByParent(groupedColumns)
      setInnerColumnsSearchable(groupedColumns)
    }
  }, [extraColumns])

  useEffect(() => {
    setActiveIndexes(parentAccessors)
  }, [parentAccessors.join(',')])

  const handleReorder = (result) => {
    const { source, destination } = result
    if (!destination) return

    const reorderedItems = reorder(tempColumns, source.index, destination.index)
    setTempColumns(reorderedItems)
    setOuterColumnsSearchable(reorderedItems)
  }

  const handleReorderInside = (result) => {
    const { source, destination } = result
    if (!destination) return

    const sourceGroup = source.droppableId
    const destinationGroup = destination.droppableId

    if (sourceGroup !== destinationGroup) return

    const items = innerColumnsGroupedByParent[sourceGroup]

    const reorderedItems = reorder(items, source.index, destination.index)

    setInnerColumnsGroupedByParent({
      ...innerColumnsGroupedByParent,
      [sourceGroup]: reorderedItems,
    })
    setInnerColumnsSearchable({
      ...innerColumnsGroupedByParent,
      [sourceGroup]: reorderedItems,
    })
  }

  const handleUpdateColumnVisibility = (accessor, isHidden) => {
    setTempColumns((prevColumns) => {
      return prevColumns.map((column) =>
        column.accessor === accessor ? { ...column, is_hidden: !isHidden } : column
      )
    })
    setOuterColumnsSearchable((prevColumns) => {
      return prevColumns.map((column) =>
        column.accessor === accessor ? { ...column, is_hidden: !isHidden } : column
      )
    })
  }

  const handleToggleInnerVisibility = (accessor, parent, isHidden) => {
    setInnerColumnsGroupedByParent((prevInnerColumnsGroupedByParent) => {
      const parentCols = prevInnerColumnsGroupedByParent[parent].map((column) =>
        column.accessor === accessor ? { ...column, is_hidden: !isHidden } : column
      )
      setInnerColumnsSearchable((prevSearchable) => ({ ...prevSearchable, [parent]: parentCols }))

      return { ...prevInnerColumnsGroupedByParent, [parent]: parentCols }
    })
  }

  const handleToggleAll = () => {
    const changedTemp = tempColumns.map((column) => {
      if (column.isParent) return column
      return { ...column, is_hidden: toggleAll }
    })
    setTempColumns(changedTemp)
    setOuterColumnsSearchable(changedTemp)
    if (!isEmpty(extraColumns)) {
      const changedInner = extraColumns?.reduce((acc, column) => {
        const parentAccessor = column.parent
        if (!acc[parentAccessor]) {
          acc[parentAccessor] = []
        }
        column.is_hidden = toggleAll
        acc[parentAccessor].push(column)
        return acc
      }, {})
      setInnerColumnsGroupedByParent(changedInner)
      setInnerColumnsSearchable(changedInner)
    }
    setToggleAll(!toggleAll)
  }

  // Right now default is all is visible, might change later
  const handleResetToDefaults = () => {
    const changedTemp = tempColumns.map((column) => {
      return { ...column, is_hidden: false }
    })
    setTempColumns(changedTemp)
    setOuterColumnsSearchable(changedTemp)

    if (!isEmpty(extraColumns)) {
      const changedInner = extraColumns?.reduce((acc, column) => {
        const parentAccessor = column.parent
        if (!acc[parentAccessor]) {
          acc[parentAccessor] = []
        }
        column.is_hidden = false
        acc[parentAccessor].push(column)
        return acc
      }, {})
      setInnerColumnsGroupedByParent(changedInner)
      setInnerColumnsSearchable(changedInner)
    }
    setToggleAll(true)
  }

  const handleOnSearch = (newSearch) => {
    setSearch(newSearch)
    const searchLowerCase = newSearch.toLowerCase()
    const result = {}
    const parentColumnsWithChildren = []
    // eslint-disable-next-line guard-for-in
    for (const key in innerColumnsGroupedByParent) {
      result[key] = innerColumnsGroupedByParent[key].filter((item) =>
        item.label.toLowerCase().includes(searchLowerCase)
      )
      const parentMatch = tempColumns.filter(
        (item) => item.accessor === key && item.label.toLowerCase().includes(searchLowerCase)
      )
      if (!isEmpty(parentMatch)) {
        result[key] = innerColumnsGroupedByParent[key]
      }
      if (result[key].length > 0) parentColumnsWithChildren.push(key)
    }
    setInnerColumnsSearchable(result)

    setOuterColumnsSearchable(
      tempColumns.filter((column) => {
        if (parentColumnsWithChildren.length === 0) {
          return column.label.toLowerCase().includes(searchLowerCase)
        }
        return (
          column.label.toLowerCase().includes(searchLowerCase) ||
          (column.isParent && parentColumnsWithChildren.includes(column.accessor))
        )
      })
    )
  }
  const handleClickAccordion = (accessor) => {
    if (activeIndexes.includes(accessor)) {
      setActiveIndexes(activeIndexes.filter((item) => item !== accessor))
    } else {
      setActiveIndexes([...activeIndexes, accessor])
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (isNil(innerColumnsGroupedByParent)) {
          handleSubmit(tempColumns)
        } else {
          handleSubmit([
            ...tempColumns,
            ...[].concat(...Object.values(innerColumnsGroupedByParent)),
          ])
        }
        onClose()
      }}
    >
      <div className="edit-columns-form">
        <div className="flex-align-center flex-space-between mb">
          <TextInput
            data-testid="edit-columns-form-search"
            type="text"
            id="edit-columns-form-search"
            placeholder="Search columns..."
            onChange={(event) => {
              handleOnSearch(event.target.value)
            }}
            value={search}
          />
          <div className="flex small-gap">
            <Button
              onClick={handleToggleAll}
              data-testid="edit-columns-form-toggle-all"
              loading={loading}
              disabled={loading}
            >
              Toggle All
            </Button>
            <Button
              onClick={handleResetToDefaults}
              data-testid="edit-columns-form-reset-defaults"
              loading={loading}
              disabled={loading}
            >
              Reset Defaults
            </Button>
          </div>
        </div>
        <div className="edit-columns-row edit-columns-form-column-header">
          <div className="label">Column Name</div>
          <div className="label">Visibility</div>
        </div>
        <div className="edit-columns-form-table">
          <DragAndDrop onDragEnd={handleReorder}>
            <Drop droppableId="droppable" isDropDisabled={!!search}>
              {outerColumnsSearchable.map((column, index) => {
                return (
                  <Drag
                    key={column.accessor}
                    draggableId={column.accessor}
                    index={index}
                    alwaysShowIcon
                    showDragStyle
                    isModal
                    dataTestId={`${column.accessor}`}
                    outer={column.isParent}
                    nestedEntriesCount={
                      column.isParent ? innerColumnsGroupedByParent[column.accessor]?.length : []
                    }
                    readOnly={!!search}
                  >
                    <div
                      data-testid={`edit-columns-row-column-${column.accessor}`}
                      className={classNames({ 'edit-columns-row': !column.isParent })}
                    >
                      {!column.isParent && (
                        <>
                          <div className="edit-columns-row-label">
                            <strong>{column.label}</strong>
                          </div>

                          <Toggle
                            className="hidden-on-drag"
                            data-testid={`edit-columns-row-checkbox-${column.accessor}`}
                            aria-label={`Show ${column.label}`}
                            checked={!column.is_hidden}
                            onChange={() => {
                              handleUpdateColumnVisibility(column.accessor, column.is_hidden)
                            }}
                          />
                        </>
                      )}

                      {column.isParent && (
                        <Accordion
                          id={column.accessor}
                          expanded={activeIndexes.includes(column.accessor)}
                          handleToggle={() => handleClickAccordion(column.accessor)}
                          title={
                            <div className="edit-column-accordion-title flex-align-center medium-gap">
                              <strong>{column.label}</strong>
                              {column.pillLabel && (
                                <Pill xs magical icon={<IconSparkles />}>
                                  {column.pillLabel}
                                </Pill>
                              )}
                            </div>
                          }
                        >
                          <DragAndDrop onDragEnd={handleReorderInside}>
                            <Drop
                              droppableId={`${column.accessor}`}
                              type="droppable-entry"
                              isDropDisabled={!!search}
                            >
                              {!isEmpty(innerColumnsSearchable[column.accessor]) ? (
                                innerColumnsSearchable[column.accessor].map(
                                  (innerColumn, innerIndex) => {
                                    return (
                                      <Drag
                                        key={innerColumn.accessor}
                                        draggableId={innerColumn.accessor}
                                        index={innerIndex}
                                        dataTestId={`${innerColumn.accessor}`}
                                        alwaysShowIcon
                                        showDragStyle
                                        isModal
                                        readOnly={!!search}
                                      >
                                        <div
                                          data-testid={`edit-columns-row-column-${innerColumn.accessor}`}
                                          className="edit-columns-row"
                                        >
                                          <div>
                                            <strong>{innerColumn.label}</strong>
                                          </div>

                                          <Toggle
                                            className="hidden-on-drag"
                                            data-testid={`edit-columns-row-checkbox-${innerColumn.accessor}`}
                                            aria-label={`Show ${innerColumn.label}`}
                                            checked={!innerColumn.is_hidden}
                                            onChange={() => {
                                              handleToggleInnerVisibility(
                                                innerColumn.accessor,
                                                innerColumn.parent,
                                                innerColumn.is_hidden
                                              )
                                            }}
                                          />
                                        </div>
                                      </Drag>
                                    )
                                  }
                                )
                              ) : (
                                <div className="muted-text" style={{ marginLeft: '2rem' }}>
                                  {column.accessor === 'scores'
                                    ? 'Select a Scorecard in Filters to add score columns'
                                    : column.accessor === 'extracted_data'
                                      ? 'No extracted data columns available'
                                      : 'No columns currently available.'}
                                </div>
                              )}
                            </Drop>
                          </DragAndDrop>
                        </Accordion>
                      )}
                    </div>
                  </Drag>
                )
              })}
            </Drop>
          </DragAndDrop>
        </div>
      </div>

      <div className="modal-footer">
        <Button data-testid="abstract-form-close" onClick={onClose}>
          Discard
        </Button>
        <Button
          data-testid="edit-columns-form-submit"
          brand
          type="submit"
          loading={loading}
          disabled={loading}
        >
          Save
        </Button>
      </div>
    </form>
  )
}
